import styled from 'styled-components';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { ArticleCaptionAndCredits } from 'editorial/components/ArticleCaptionAndCredits';
import { GREATEST } from 'editorial/enums/constants';
import { IArticleIntroductionSection, IArticleSectionAdditionalProps } from 'editorial/types';
import { Image } from 'shared/components/Image';
import { breakpoints } from 'styles/theme/breakpoints';
import { createSanitizedHtmlObject } from 'shared/utils/createSanitizedHtml';
import { variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';
import { localizeUrl } from 'localization/utils/localizeUrl';

const TP = 'editorial.components.ArticleIntroduction';

export const ArticleIntroduction = ({
  teaser,
  pictures,
  subject,
  introduction,
  caption,
  credits,
  category,
}: IArticleIntroductionSection & IArticleSectionAdditionalProps) => {
  const { locale } = useRouter();
  const title =
    category === GREATEST ? t(`${TP}.title`, `GREATEST: {subject}`, { subject }) : subject;
  const heroImage = pictures?.[0]?.url;

  return (
    <>
      <ArticleIntroduction.Hero>
        <ArticleIntroduction.HeroText>
          <div>
            {category === 'resource' && (
              <Link href="/editorial/resources" passHref legacyBehavior>
                <ArticleIntroduction.ResourcesLink>
                  {t(`${TP}.resources`, 'Resources')}
                </ArticleIntroduction.ResourcesLink>
              </Link>
            )}
            <ArticleIntroduction.HeroHeader>{title}</ArticleIntroduction.HeroHeader>
            <ArticleIntroduction.HeroBlurb
              dangerouslySetInnerHTML={createSanitizedHtmlObject(teaser)}
            />
          </div>
          {/* We're repurposing the image caption as the credits */}
          <div>
            {caption && (
              <ArticleIntroduction.Credits
                dangerouslySetInnerHTML={createSanitizedHtmlObject(caption)}
              />
            )}
          </div>
        </ArticleIntroduction.HeroText>
        {heroImage && (
          <ArticleIntroduction.ImageContainer>
            <ArticleIntroduction.HeroImage
              alt="hero"
              noSrcModification={heroImage?.toLowerCase()?.includes('.gif')}
              src={heroImage}
              sizes={`(max-width: ${breakpoints.medium}px) 80vw, 35vw`}
            />
            {credits && <ArticleCaptionAndCredits credits={credits} />}
          </ArticleIntroduction.ImageContainer>
        )}
      </ArticleIntroduction.Hero>
      {introduction && (
        <ArticleIntroduction.TextSection
          dangerouslySetInnerHTML={createSanitizedHtmlObject(localizeUrl(introduction, locale))}
        />
      )}
    </>
  );
};

ArticleIntroduction.Hero = styled.div`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.black};
  padding: 0;
  background-color: transparent;
  margin: 24px 0 0;

  a {
    text-decoration: underline;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    margin: 40px 0 0;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    max-height: calc(100vh - 105px);
    flex-direction: row;
    justify-content: space-between;
    margin: 40px 0 120px;
  }
`;

ArticleIntroduction.HeroText = styled.div`
  display: flex;
  flex: 0 1 30%;
  flex-direction: column;
  word-break: break-word;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    justify-content: space-between;
    margin-right: 175px;
    flex: 1;
    padding: 0;
    max-width: 762px;
  }
`;

ArticleIntroduction.ResourcesLink = styled.a`
  ${variableFontRegular({ size: '11px', height: '15px', spacing: '0' })};
  text-transform: uppercase;
  text-decoration: underline;
  text-underline-offset: 2px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFontRegular({ size: '13px', height: '15px', spacing: '0' })};
  }
`;

ArticleIntroduction.HeroHeader = styled.h1`
  ${variableFontRegular({ size: '36', height: '0.97', spacing: '-0.5' })};
  margin: 12px 0 30px;
  text-transform: uppercase;
  font-weight: 400;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    margin: 12px 0 40px;
    ${variableFontRegular({ size: '48', height: '0.97', spacing: '-0.5' })};
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    margin: 12px 0 70px;
    ${variableFontRegular({ size: '60', height: '0.97', spacing: '-0.5 ' })};
  }
`;

ArticleIntroduction.HeroBlurb = styled.div`
  p {
    ${variableFontRegular({ size: '18', height: '22px', spacing: '-0.15' })};
    text-transform: uppercase;
    margin-bottom: 20px;

    @media screen and (min-width: ${({ theme }) => theme.breakpoints.small}px) {
      margin-bottom: 40px;
    }
  }
`;

ArticleIntroduction.HeroImage = styled(Image)`
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    width: auto;
  }
`;

ArticleIntroduction.ImageContainer = styled.div`
  flex: 1 0 60%;
  margin: 50px 0;
  width: 100%;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    margin: 40px 0;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex: 0 0 35%;
    margin: 10px 0 0;
    height: 100%;
    max-height: 820px;
  }
`;

ArticleIntroduction.Credits = styled.div`
  ${variableFontRegular({ size: '12', height: '14px', spacing: '-0.15' })};
  text-transform: uppercase;
  margin: 0;
  white-space: pre-wrap;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFontRegular({ size: '14', height: '16px', spacing: '-0.15' })};
  }
`;

ArticleIntroduction.TextSection = styled.div`
  ${variableFontRegular({ size: '16', height: '25px', spacing: '-0.15' })};

  a {
    text-decoration: underline;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFontRegular({ size: '18', height: '25px', spacing: '-0.15' })};
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    max-width: 720px;
  }
`;
